/**
 * UserMetricsCard Component - User acquisition and transaction metrics
 */
import React from 'react';
import type { UserMetrics, TransactionMetrics } from './types';

interface UserMetricsCardProps {
  userMetrics: UserMetrics;
  transactionMetrics: TransactionMetrics;
  loading?: boolean;
}

export const UserMetricsCard: React.FC<UserMetricsCardProps> = ({
  userMetrics, transactionMetrics, loading
}) => {
  if (loading) {
    return (
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 animate-pulse">
        <div className="h-4 bg-gray-700 rounded w-32 mb-4" />
        <div className="grid grid-cols-2 gap-4">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-16 bg-gray-700 rounded" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
      <h3 className="text-white font-semibold mb-4">👥 User & Transaction Metrics</h3>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {/* User Metrics */}
        <div className="bg-gray-700/50 rounded-lg p-3">
          <p className="text-white text-sm">New Users</p>
          <p className="text-2xl font-bold text-blue-400">{userMetrics.newUsers.toLocaleString()}</p>
        </div>
        <div className="bg-gray-700/50 rounded-lg p-3">
          <p className="text-white text-sm">Active Users</p>
          <p className="text-2xl font-bold text-green-400">{userMetrics.activeUsers.toLocaleString()}</p>
        </div>
        <div className="bg-gray-700/50 rounded-lg p-3">
          <p className="text-white text-sm">First Purchase</p>
          <p className="text-2xl font-bold text-purple-400">{userMetrics.firstPurchaseUsers.toLocaleString()}</p>
        </div>
        <div className="bg-gray-700/50 rounded-lg p-3">
          <p className="text-white text-sm">Conversion Rate</p>
          <p className="text-2xl font-bold text-amber-400">{userMetrics.conversionRate.toFixed(1)}%</p>
        </div>
      </div>

      <h4 className="text-white text-sm font-medium mb-3">Transaction Analysis</h4>

      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-gray-700/50 rounded-lg p-3">
          <p className="text-white text-sm">Avg Order Value</p>
          <p className="text-xl font-bold text-white">${transactionMetrics.avgOrderValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
        </div>
        <div className="bg-gray-700/50 rounded-lg p-3">
          <p className="text-white text-sm">Median Order</p>
          <p className="text-xl font-bold text-white">${transactionMetrics.medianOrderValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
        </div>
        <div className="bg-gray-700/50 rounded-lg p-3">
          <p className="text-white text-sm">Total Transactions</p>
          <p className="text-xl font-bold text-white">{transactionMetrics.transactionCount.toLocaleString()}</p>
        </div>
      </div>

      {/* Distribution */}
      {transactionMetrics.distribution.length > 0 && (
        <div>
          <p className="text-white text-sm mb-2">Transaction Size Distribution</p>
          <div className="flex gap-1 h-12">
            {transactionMetrics.distribution.map((bucket, i) => {
              const maxCount = Math.max(...transactionMetrics.distribution.map(b => b.count), 1);
              const height = (bucket.count / maxCount) * 100;
              return (
                <div key={i} className="flex-1 flex flex-col justify-end group relative">
                  <div 
                    className="bg-blue-500/50 hover:bg-blue-500 rounded-t transition-colors"
                    style={{ height: `${height}%`, minHeight: bucket.count > 0 ? '4px' : '0' }}
                  />
                  <p className="text-[8px] text-gray-500 text-center truncate mt-1">{bucket.range}</p>
                  <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-gray-900 px-2 py-1 rounded text-sm hidden group-hover:block whitespace-nowrap">
                    {bucket.count} txns
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMetricsCard;

